import I18n from 'I18n';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { useState, useRef, useEffect } from 'react';
import { SearchAutocompleteResults, getPopularTermUrl } from './SearchAutocompleteResults';
import { useHistory, useLocation } from 'react-router-dom';
import { makeInlineContentCardUrl } from './SearchAutocompleteResults';
import { useGetAutocompleteSuggestionsQuery } from '../../../__generated__/chirp/com/hubspot/academy/autocomplete/rpc/AcademyAutocompleteChirpService.hooks';
import getLang from 'academy-common-lib/utils/getLang';
import useDebouncedValue from 'academy-common-lib/hooks/useDebouncedValue';
import { MIN_SEARCH_LENGTH, useACSearchInputContext } from './ACSearchInputProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const QUICK_LINKS_LIMIT = 3;
const POPULAR_TERMS_LIMIT = 6;
const DEBOUNCE_DELAY = 400;
export const ACSearchInput = props => {
  const {
    searchValue,
    suggestions,
    setSuggestions,
    handleSearchValueChange,
    isAutocompleteSuggestionSelected,
    setIsAutocompleteSuggestionSelected
  } = useACSearchInputContext();
  const [isPopoverOpening, setIsPopoverOpening] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState();
  const inputRef = useRef(null);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const debouncedSearchValue = useDebouncedValue(searchValue, DEBOUNCE_DELAY);
  useGetAutocompleteSuggestionsQuery({
    skip: debouncedSearchValue.length < MIN_SEARCH_LENGTH,
    variables: {
      queryTerm: debouncedSearchValue,
      language: getLang().toUpperCase(),
      popularTermsLimit: POPULAR_TERMS_LIMIT,
      quickLinksLimit: QUICK_LINKS_LIMIT
    },
    onCompleted: ({
      result: newSuggestions
    }) => {
      const hasResults = newSuggestions.popularTerms.length > 0 || newSuggestions.quickLinks.length > 0;
      if (hasResults) {
        setHighlightedOptionIndex(null);
        if (!isAutocompleteSuggestionSelected) {
          setIsPopoverOpening(true);
        }
        setSuggestions(newSuggestions);
      } else {
        setSuggestions(undefined);
      }
    }
  });
  const handleKeyDown = e => {
    if (!isPopoverOpening || !suggestions) return;
    const totalOptions = suggestions.popularTerms.length + suggestions.quickLinks.length;
    switch (e.key) {
      case 'ArrowUp':
        {
          e.preventDefault();
          if (highlightedOptionIndex === null || highlightedOptionIndex <= 0) {
            setHighlightedOptionIndex(totalOptions - 1);
          } else {
            setHighlightedOptionIndex(highlightedOptionIndex - 1);
          }
          break;
        }
      case 'ArrowDown':
        {
          e.preventDefault();
          if (highlightedOptionIndex === null || highlightedOptionIndex >= totalOptions - 1) {
            setHighlightedOptionIndex(0);
          } else {
            setHighlightedOptionIndex(highlightedOptionIndex + 1);
          }
          break;
        }
      case 'Enter':
        {
          var _inputRef$current;
          e.preventDefault();
          (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
          if (highlightedOptionIndex !== null) {
            if (highlightedOptionIndex < suggestions.popularTerms.length) {
              const highlightedOption = suggestions.popularTerms[highlightedOptionIndex];
              handleSearchValueChange(highlightedOption.term);
              history.push(getPopularTermUrl(highlightedOption.term));
            } else {
              const highlightedOption = suggestions.quickLinks[highlightedOptionIndex - suggestions.popularTerms.length];
              history.push(makeInlineContentCardUrl(highlightedOption, location));
              handleSearchValueChange(highlightedOption.resourceName);
            }
          }
          setIsAutocompleteSuggestionSelected(true);
          setIsPopoverOpening(false);
          break;
        }
      default:
        break;
    }
  };
  const handleResultItemClick = option => {
    var _option$text;
    handleSearchValueChange((_option$text = option.text) !== null && _option$text !== void 0 ? _option$text : '');
    setIsAutocompleteSuggestionSelected(true);
    setIsPopoverOpening(false);
  };
  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const updatePopoverWidth = () => {
      setPopoverWidth(input.offsetWidth);
    };
    updatePopoverWidth();
    const resizeObserver = new ResizeObserver(updatePopoverWidth);
    resizeObserver.observe(input);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    autoPlacement: false,
    content: suggestions ? /*#__PURE__*/_jsx(SearchAutocompleteResults, {
      highlightedOptionIndex: highlightedOptionIndex,
      onResultItemMouseEnter: (__option, index) => {
        setHighlightedOptionIndex(index);
      },
      onResultItemClick: handleResultItemClick,
      suggestions: suggestions
    }) : null,
    inlineSize: popoverWidth !== undefined ? `${popoverWidth}px` : 'auto',
    onOpenChange: e => {
      setIsPopoverOpening(e.target.value);
      setIsAutocompleteSuggestionSelected(!e.target.value);
    },
    open: isPopoverOpening && Boolean(suggestions),
    placement: "bottom right",
    children: /*#__PURE__*/_jsx(SearchInput, Object.assign({
      "data-test-id": "filter-type-search",
      inputRef: inputRef,
      onChange: e => {
        handleSearchValueChange(e.target.value);
      },
      onClick: () => {
        setIsAutocompleteSuggestionSelected(false);
        if (!isPopoverOpening) {
          setHighlightedOptionIndex(null);
        }
        setIsPopoverOpening(true);
      },
      onKeyDown: handleKeyDown,
      placeholder: I18n.text('academy-navigation-lib.ACPage.header.search.placeholder'),
      value: searchValue
    }, props))
  });
};